// src/pages/Orders/OrderForm/OrderFormSections/ExistingAdjustmentsDisplay.jsx
import React, { useState } from "react";
import Badge from "../../../../components/common/Badge";
import {
  FiPercent,
  FiTag,
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiCheck,
  FiClock,
  FiDollarSign,
} from "react-icons/fi";

const ExistingAdjustmentsDisplay = ({
  source,
  cartAdjustments = [],
  sessionAdjustments = [],
  formatCurrency,
}) => {
  const [expanded, setExpanded] = useState(true);

  // Don't show if no adjustments
  const hasCartAdjustments = cartAdjustments && cartAdjustments.length > 0;
  const hasSessionAdjustments =
    sessionAdjustments && sessionAdjustments.length > 0;

  if (!hasCartAdjustments && !hasSessionAdjustments) {
    return null;
  }

  const totalAdjustments =
    (cartAdjustments?.length || 0) + (sessionAdjustments?.length || 0);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FiPercent className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">
              Existing Adjustments ({totalAdjustments})
            </h3>
            <p className="text-sm text-gray-600">
              From {source === "cart" ? "cart" : "consultation session"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="info" size="sm">
            Read-only
          </Badge>
          {expanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            {/* Info Banner */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-2">
                <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">
                    These adjustments were applied in the{" "}
                    {source === "cart" ? "cart" : "consultation session"}
                  </p>
                  <p className="text-xs text-blue-700">
                    They are read-only and will be automatically applied to the
                    order. You can add additional order-level adjustments below
                    if needed.
                  </p>
                </div>
              </div>
            </div>

            {/* Cart Adjustments */}
            {hasCartAdjustments && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <FiTag className="w-4 h-4 text-green-600" />
                  <h4 className="font-semibold text-gray-900">
                    Cart Coupons & Discounts
                  </h4>
                  <Badge variant="success" size="sm">
                    {cartAdjustments.length}
                  </Badge>
                </div>

                {cartAdjustments.map((adjustment, idx) => (
                  <CartAdjustmentCard
                    key={idx}
                    adjustment={adjustment}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            )}

            {/* Session Adjustments */}
            {hasSessionAdjustments && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <FiPercent className="w-4 h-4 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">
                    Session Price Adjustments
                  </h4>
                  <Badge variant="purple" size="sm">
                    {sessionAdjustments.length}
                  </Badge>
                </div>

                {sessionAdjustments.map((adjustment, idx) => (
                  <SessionAdjustmentCard
                    key={adjustment.adjustmentId || idx}
                    adjustment={adjustment}
                    formatCurrency={formatCurrency}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Cart Adjustment Card
const CartAdjustmentCard = ({ adjustment, formatCurrency }) => {
  // If adjustment is just a coupon code string
  if (typeof adjustment === "string") {
    return (
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FiTag className="w-4 h-4 text-green-600" />
            <div>
              <p className="text-sm font-semibold text-green-900">
                {adjustment}
              </p>
              <p className="text-xs text-green-700">Coupon Code</p>
            </div>
          </div>
          <Badge variant="success" size="sm">
            <FiCheck className="w-3 h-3 mr-1" />
            Applied
          </Badge>
        </div>
      </div>
    );
  }

  // If adjustment is an object with details
  return (
    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <FiTag className="w-4 h-4 text-green-600 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-green-900 truncate">
              {adjustment.code || adjustment.name || "Cart Discount"}
            </p>
            <p className="text-xs text-green-700">
              {adjustment.type || "Coupon"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {adjustment.discount && (
            <span className="text-sm font-medium text-green-700">
              -{formatCurrency(adjustment.discount)}
            </span>
          )}
          <Badge variant="success" size="sm">
            Applied
          </Badge>
        </div>
      </div>

      {adjustment.description && (
        <p className="text-xs text-green-600 mt-1">{adjustment.description}</p>
      )}
    </div>
  );
};

// Session Adjustment Card
const SessionAdjustmentCard = ({ adjustment, formatCurrency }) => {
  const getAdjustmentValue = () => {
    if (!adjustment.adjustment) return "";

    if (adjustment.adjustment.type === "PERCENTAGE") {
      return `${adjustment.adjustment.value}% off`;
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      return `${formatCurrency(adjustment.adjustment.value)} off`;
    } else if (adjustment.adjustment.type === "OVERRIDE_PRICE") {
      return `Override to ${formatCurrency(
        adjustment.adjustment.overridePrice
      )}`;
    }
    return "";
  };

  const getStatusColor = () => {
    if (adjustment.status === "ACTIVE") return "success";
    if (adjustment.status === "PENDING") return "yellow";
    return "gray";
  };

  const getScopeIcon = () => {
    if (adjustment.scope === "ITEM") return "🎁";
    if (adjustment.scope === "CART") return "🛒";
    if (adjustment.scope === "EXCHANGE_ITEM") return "🔄";
    return "📝";
  };

  return (
    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <span className="text-lg flex-shrink-0">{getScopeIcon()}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-purple-900 truncate">
              {adjustment.adjustmentType?.replace(/_/g, " ") ||
                "Price Adjustment"}
            </p>
            <p className="text-xs text-purple-700 truncate">
              {adjustment.reason}
            </p>
            {adjustment.scope && (
              <p className="text-xs text-purple-600 mt-0.5">
                Scope: <strong>{adjustment.scope}</strong>
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
          <span className="text-sm font-medium text-purple-700 whitespace-nowrap">
            {getAdjustmentValue()}
          </span>
          <Badge variant={getStatusColor()} size="sm">
            {adjustment.status}
          </Badge>
        </div>
      </div>

      {/* Approval Info */}
      {adjustment.approved && adjustment.approvedBy && (
        <div className="flex items-center gap-1 text-xs text-purple-600 mt-2 pt-2 border-t border-purple-300">
          <FiCheck className="w-3 h-3" />
          <span>
            Approved by {adjustment.approvedBy}
            {adjustment.approvedAt && (
              <span className="ml-1">
                on {new Date(adjustment.approvedAt).toLocaleDateString()}
              </span>
            )}
          </span>
        </div>
      )}

      {/* Target Product */}
      {adjustment.targetProductId && (
        <div className="flex items-center gap-1 text-xs text-purple-600 mt-1">
          <FiDollarSign className="w-3 h-3" />
          <span>Applied to specific product</span>
        </div>
      )}

      {/* Notes */}
      {adjustment.notes && (
        <p className="text-xs text-purple-600 mt-2 pt-2 border-t border-purple-300">
          {adjustment.notes}
        </p>
      )}
    </div>
  );
};

export default ExistingAdjustmentsDisplay;
